import { useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./plantViewDetails.css";
import { format } from "date-fns";
import { PROD } from "../../..//common/constants";
import BackButton from "../../../common/backButton";
 
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Stack,
  Divider,
  Chip,
  IconButton,
  Tooltip
} from "@mui/material";
 
import { DataGrid } from "@mui/x-data-grid";
import { ViewIcon } from "../../../common/icons";
 
function PlantViewDetails() {
  const navigate = useNavigate();
  const { state } = useLocation();
 
  const plantationBatchDetails =
    state?.workFlowRequestDetails?.plantationBatchDtlsLst?.[0] || {};
 
  const plantationSpeciesDetails = plantationBatchDetails?.species || [];
 
  const formatDate = (date) => {
    if (!date) return "-";
    try {
      return format(new Date(date), "dd/MM/yyyy");
    } catch (error) {
      return "-";
    }
  };
 
  const rows = useMemo(() => {
    return plantationSpeciesDetails.map((item, index) => ({
      id: item?.id || `${item?.species || "species"}-${index}`,
      ...item
    }));
  }, [plantationSpeciesDetails]);
 
  const columns = [
    {
      field: "species",
      headerName: "Species",
      flex: 1,
      minWidth: 140
    },
    {
      field: "saplingType",
      headerName: "Sapling Type",
      flex: 1,
      minWidth: 150
    },
    {
      field: "noOfClumps",
      headerName: "No. of Clumps",
      flex: 1,
      minWidth: 140
    },
    {
      field: "saplingQuantity",
      headerName: "Sapling Quantity",
      flex: 1,
      minWidth: 150
    },
    {
      field: "dateOfPlantation",
      headerName: "Date of Plantation",
      flex: 1,
      minWidth: 160,
      renderCell: (params) => formatDate(params.row?.dateOfPlantation)
    },
    {
      field: "maturedDate",
      headerName: "Plantation Matured Date",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => formatDate(params.row?.maturedDate)
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 130,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip title="view">
        <IconButton
          size="small"
          variant="contained"
          color="primary"
          onClick={() =>
            navigate("plantVerifyDetails", {
              state: {
                batchItem: plantationBatchDetails,
                speciesItem: params.row,
                type: state?.type
              }
            })
          }
        >
          <ViewIcon/>
        </IconButton>
        </Tooltip>
      )
    }
  ];
 
  return (
    <Box sx={{ minHeight: "100vh", py: 3 }}>
      <Container maxWidth="xl">
        {!PROD && (
          <Typography
            className="pageNumberingResc"
            sx={{
              textAlign: "center",
              fontSize: "12px",
              color: "text.secondary",
              mb: 1
            }}
          >
            RES25
          </Typography>
        )}
 
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            mb: 3,
            borderRadius: 2,
            border: "1px solid #e0e0e0",
            backgroundColor: "#ffffff"
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={2}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <BackButton navigate={navigate} />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Plantation Batch Details
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  View batch information and species details
                </Typography>
              </Box>
            </Stack>
 
            <Chip
              label={`Operation: ${plantationBatchDetails?.workflowRqstDtls?.operation || "-"}`}
              color="primary"
              variant="outlined"
            />
          </Stack>
        </Paper>
 
        {/* Summary Card */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 2,
            border: "1px solid #e0e0e0",
            backgroundColor: "#ffffff"
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Batch Summary
          </Typography>
          <Divider sx={{ mb: 2.5 }} />
 
          <Grid container spacing={2}>
            <Grid item xs={12} md={6} lg={3}>
              <Typography variant="body2" color="text.secondary">
                Plantation Batch No.
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {plantationBatchDetails?.plantationBatchNo || "-"}
              </Typography>
            </Grid>
 
            <Grid item xs={12} md={6} lg={3}>
              <Typography variant="body2" color="text.secondary">
                Grower URN
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {plantationBatchDetails?.urn || "-"}
              </Typography>
            </Grid>
 
            <Grid item xs={12} md={6} lg={3}>
              <Typography variant="body2" color="text.secondary">
                Name
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {plantationBatchDetails?.name || "-"}
              </Typography>
            </Grid>
 
            <Grid item xs={12} md={6} lg={3}>
              <Typography variant="body2" color="text.secondary">
                Land Name
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {plantationBatchDetails?.landName || "-"}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
 
        {/* Species DataGrid */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 2,
            border: "1px solid #e0e0e0",
            backgroundColor: "#ffffff"
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Species Details
          </Typography>
 
          <Box sx={{ width: "100%" }}>
            <DataGrid
              rows={rows}
              columns={columns}
              autoHeight
              disableRowSelectionOnClick
              pageSizeOptions={[5, 10, 20]}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 10, page: 0 }
                }
              }}
              sx={{
                border: "none",
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#f8f9fb",
                  fontWeight: 600
                },
                "& .MuiDataGrid-cell": {
                  alignItems: "center"
                },
                "& .MuiDataGrid-footerContainer": {
                  borderTop: "1px solid #e0e0e0"
                }
              }}
            />
          </Box>
        </Paper>
 
        {/* Action Buttons */}
        {state?.type !== "sent" && (
          <Box display="flex" justifyContent="flex-end" mt={3}>
            <Button
              variant="contained"
              sx={{ minWidth: 120 }}
              onClick={() =>
                navigate("approveOrRejectActionPage", {
                  state: {
                    selectedApplicationNumber:
                      state?.workFlowRequestDetails?.plantationBatchDtlsLst?.[0]
                        ?.applicationNumber,
                    workflowdtl:
                      state?.workFlowRequestDetails?.plantationBatchDtlsLst?.[0]
                        ?.workflowRqstDtls || null,
                    type: "plant",
                    category: state?.type
                  }
                })
              }
            >
              Next
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
}
 
export default PlantViewDetails;